// GenerationsShell — shared chrome for MagicDesk / Super Narrate / Deskeynomics.
// Handles: list saved generations, create-new panel, rename, delete, super-credit
// chip, error/loading states. The active generation's body is rendered via the
// `renderBody` prop so each kind keeps its own UX distinct.
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Zap, Trash2, Pencil, Check, X, RefreshCw, Save } from 'lucide-react';
import api from '../../studentApi';
import { toast } from 'sonner';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return ''; }
};

export default function GenerationsShell({
  lessonId, kind, kindLabel, tint, costLabel, defaultCost,
  setupPanel,    // (onGenerate, generating) => JSX for the create-new form
  renderBody,    // (gen, ctx) => JSX for the main content area for an active gen
  emptyHint,     // small string under "No saved {kindLabel}s yet"
  showCreditsChip = true,   // MagicDesk/Super Narrate hide this — Super Credits live only in the header now
}) {
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeGen, setActiveGen] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingGen, setLoadingGen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');

  const refreshList = async () => {
    setLoadingList(true);
    try {
      const r = await api.get(`/student/lesson/${lessonId}/generations?kind=${kind}`);
      const items = r.generations || [];
      setList(items);
      return items;
    } catch (e) {
      toast.error(e.message || 'Failed to load saved');
      return [];
    } finally { setLoadingList(false); }
  };
  const refreshCredits = async () => {
    try { const r = await api.get('/student/credits/peek'); setCredits(r.super_credits); } catch {}
  };

  useEffect(() => {
    let cancelled = false;
    setActiveId(null); setActiveGen(null);
    (async () => {
      // Auto-resume: the backend keeps generating even if the student closes
      // the tab or the whole browser — on reopen, silently reattach to
      // whichever generation of this kind is still in flight instead of
      // dropping them at a blank Setup screen they'd otherwise only escape
      // by manually finding it in the sidebar list.
      const items = await refreshList();
      if (cancelled) return;
      const inProgress = items.find(g => g.status === 'generating');
      if (inProgress) setActiveId(inProgress.id);
    })();
    refreshCredits();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, kind]);

  useEffect(() => {
    if (!activeId) { setActiveGen(null); return; }
    let alive = true;
    setLoadingGen(true);
    api.get(`/student/generations/${activeId}`)
      .then(g => { if (alive) setActiveGen(g); })
      .catch(e => alive && toast.error(e.message || 'Failed to open'))
      .finally(() => alive && setLoadingGen(false));
    return () => { alive = false; };
  }, [activeId]);

  // ── Poll while generation is in-flight ──────────────────────────────
  // Long-running pipelines (e.g. Deskeynomics Pro Comic Studio) return
  // immediately with status='generating'. We hit a lightweight `/status`
  // endpoint every 4s until the pipeline reports ready / partial / failed,
  // then re-fetch the full doc so the renderer gets panels + audio paths.
  useEffect(() => {
    if (!activeGen || activeGen.status !== 'generating') return;
    let alive = true;
    let timer = null;
    const tick = async () => {
      if (!alive) return;
      try {
        const s = await api.get(`/student/generations/${activeGen.id}/status`);
        if (!alive) return;
        if (s.status === 'generating') {
          setActiveGen(prev => prev && prev.id === activeGen.id
            ? { ...prev, progress: s.progress, status: s.status }
            : prev);
          timer = setTimeout(tick, 4000);
        } else {
          // Done — fetch the full doc with panels/images/audio paths
          const full = await api.get(`/student/generations/${activeGen.id}`);
          if (!alive) return;
          setActiveGen(full);
          setList(l => l.map(x => x.id === full.id
            ? { ...x, status: full.status, title: full.title, preview: full.preview, meta: full.meta, updated_at: full.updated_at }
            : x));
          if (s.status === 'failed') {
            toast.error(s.progress?.error || 'Generation failed');
          } else if (s.status === 'partial') {
            toast.warning('Comic ready with some failures');
          } else {
            toast.success('Comic ready!');
          }
        }
      } catch {
        // network blip — retry once more
        if (alive) timer = setTimeout(tick, 6000);
      }
    };
    timer = setTimeout(tick, 3000);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [activeGen?.id, activeGen?.status]);

  const handleGenerate = async (path, payload) => {
    setGenerating(true);
    try {
      const r = await api.post(path, payload || {});
      const gen = r.generation;
      if (gen?.id) {
        // optimistic prepend so the row appears even before refreshList finishes
        setList(l => [{ id: gen.id, kind: gen.kind, title: gen.title, status: gen.status,
                        preview: gen.preview, created_at: gen.created_at, updated_at: gen.updated_at,
                        meta: gen.meta }, ...l]);
        setActiveGen(gen);
        setActiveId(gen.id);
        if (typeof r.super_credits_remaining === 'number') setCredits(r.super_credits_remaining);
        if (gen.status === 'generating') {
          toast.success(`${kindLabel} queued · we'll auto-update as panels finish`);
        } else {
          toast.success(`${kindLabel} ready · ${r.cost ?? defaultCost} credits spent`);
        }
      }
      return r;
    } catch (e) {
      toast.error(e.message || 'Generation failed');
      // Swallow the rejection here — the toast IS the user feedback. Re-throwing
      // would surface as an unhandled promise rejection (React dev overlay)
      // because Setup-style call sites fire-and-forget the generate call.
      return { error: e };
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(`Delete this ${kindLabel}? This cannot be undone.`)) return;
    try {
      await api.delete(`/student/generations/${id}`);
      setList(l => l.filter(x => x.id !== id));
      if (activeId === id) { setActiveId(null); setActiveGen(null); }
      toast.success('Deleted');
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const handleRename = async (id) => {
    const t = (renameTitle || '').trim();
    if (!t) { setRenamingId(null); return; }
    try {
      await api.put(`/student/generations/${id}/rename`, { title: t });
      setList(l => l.map(x => x.id === id ? { ...x, title: t } : x));
      if (activeGen?.id === id) setActiveGen({ ...activeGen, title: t });
      setRenamingId(null);
      toast.success('Renamed');
    } catch (e) { toast.error(e.message || 'Rename failed'); }
  };

  const ctx = useMemo(() => ({
    refreshActive: async () => {
      if (!activeId) return;
      try { setActiveGen(await api.get(`/student/generations/${activeId}`)); } catch {}
    },
    setActiveGen,
    refreshList,
    refreshCredits,
    setCredits,
  }), [activeId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4" data-testid={`gen-shell-${kind}`}>
      {/* ── Sidebar: saved generations + create-new ─────────── */}
      <aside className="space-y-3">
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: 'var(--st-border)' }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: tint }} />
            <h3 className="text-sm font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>{kindLabel}</h3>
            {showCreditsChip && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border" style={{ borderColor: 'var(--st-border)' }} data-testid={`${kind}-credits-chip`}>
                <Zap className="w-3 h-3" style={{ color: tint }} />
                {credits == null ? '—' : credits}
              </span>
            )}
          </div>
          <button onClick={() => { setActiveId(null); setActiveGen(null); }}
            data-testid={`${kind}-new`}
            className="w-full px-4 py-2.5 text-left flex items-center gap-2 border-b text-[12.5px] font-bold transition-all hover:bg-black/5"
            style={{ borderColor: 'var(--st-border)', color: !activeId ? tint : 'var(--st-ink)' }}>
            <Plus className="w-4 h-4" /> Create new {kindLabel.toLowerCase()}
          </button>
          {loadingList ? (
            <div className="px-4 py-3 text-[11px]" style={{ color: 'var(--st-mute)' }}>Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-4 py-3 text-[11px]" style={{ color: 'var(--st-mute)' }}>No saved {kindLabel.toLowerCase()}s yet. {emptyHint}</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--st-border)' }}>
              {list.map(g => {
                const a = activeId === g.id;
                const isRen = renamingId === g.id;
                return (
                  <li key={g.id} className="px-2 py-1.5 group relative" style={{ background: a ? `color-mix(in srgb, ${tint} 16%, transparent)` : 'transparent' }} data-testid={`${kind}-row-${g.id}`}>
                    {isRen ? (
                      <div className="flex items-center gap-1">
                        <input value={renameTitle} onChange={e => setRenameTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(g.id); if (e.key === 'Escape') setRenamingId(null); }}
                          autoFocus
                          data-testid={`${kind}-rename-input-${g.id}`}
                          className="flex-1 rounded-lg px-2 py-1 text-[12px] outline-none border"
                          style={{ background: 'var(--st-bg)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }} />
                        <button onClick={() => handleRename(g.id)} className="w-7 h-7 rounded-md flex items-center justify-center" data-testid={`${kind}-rename-confirm-${g.id}`}>
                          <Check className="w-3.5 h-3.5" style={{ color: '#16A34A' }} />
                        </button>
                        <button onClick={() => setRenamingId(null)} className="w-7 h-7 rounded-md flex items-center justify-center">
                          <X className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setActiveId(g.id)} className="w-full text-left rounded-lg px-2 py-1.5">
                        <div className="text-[12px] font-bold leading-tight truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{g.title}</div>
                        <div className="text-[10px] opacity-70 mt-0.5 inline-flex items-center gap-1.5">
                          {fmtTime(g.created_at)}
                          {g.status && g.status !== 'ready' && <span className="px-1 rounded bg-amber-500/20 text-amber-700 capitalize">{g.status}</span>}
                        </div>
                      </button>
                    )}
                    {!isRen && (
                      <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5">
                        <button onClick={() => { setRenamingId(g.id); setRenameTitle(g.title); }}
                          data-testid={`${kind}-rename-${g.id}`}
                          title="Rename" className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-black/10">
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(g.id)}
                          data-testid={`${kind}-delete-${g.id}`}
                          title="Delete" className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-red-500/20">
                          <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* ── Main: setup or active generation ─────────── */}
      <section className="min-w-0">
        {!activeId ? (
          <div className="rounded-2xl border p-5" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid={`${kind}-setup`}>
            {setupPanel && setupPanel({ onGenerate: handleGenerate, generating, costLabel })}
          </div>
        ) : loadingGen || !activeGen ? (
          <div className="rounded-2xl border p-10 text-center text-sm" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', color: 'var(--st-mute)' }}>
            Opening…
          </div>
        ) : (
          renderBody && renderBody(activeGen, ctx)
        )}
      </section>
    </div>
  );
}
